import { ErrorView } from "@/components/ui/layout/error/errorView"
import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { useTrainingProgramFilterActions } from "@/modules/training/stores/useTrainingProgramFilterStore"
import { LoadingSpinner } from "@/modules/ui/components/loading"
import { CANCELED_BY_USER } from "@modules/common/types/errorTypes"
import { ConfigService } from "@modules/core/services/configService"
import { useServicesContext } from "@modules/core/services/services.context"
import { word } from "@modules/core/utils/i18n"
import { getFormatedDateTime } from "@modules/language/languageUtils"
import { getMySpacePath } from "@modules/navigation/hooks/useMySpaceNavigate"
import { Path } from "@modules/navigation/routes"
import { CheckoutContainer } from "@modules/payment/components/checkoutContainer"
import { useCheckoutPayment } from "@modules/payment/hooks/useCheckoutPayment"
import { useStripePlatform } from "@modules/payment/hooks/useStripePlatform"
import { ProgramBundleType } from "@modules/programBundle/api/programBundleDto"
import { ProgramBundleDetailsTabs } from "@modules/programBundle/components/programBundleInfoWrapper"
import { ProgramBundleEntity } from "@modules/programBundle/entities/programBundleEntity"
import { useProgramBundleRights } from "@modules/programBundle/hooks/useProgramBundleRights"
import { ProgramBundleSubscriberEntity } from "@modules/programBundle/programBundleSubscribers/entities/programBundleSubscriberEntity"
import { ProgramSubscriptionStatus } from "@modules/programs/types/programTypes"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { useConfirmPopup } from "@modules/ui/components/popup/huConfirmPopup"
import { LoggerType } from "@modules/utils/loggerUtils"
import { loadStripe } from "@stripe/stripe-js"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Suspense, useState } from "react"
import { ErrorBoundary } from "react-error-boundary"

export const useProgramBundleSubscription = (programBundleId: string) => {
  const { programBundleSubscriberApi } = useServicesContext()
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ["programBundleSubscription", programBundleId],
    queryFn: async () => {
      const result = await programBundleSubscriberApi.getProgramBundleSubscription(programBundleId)
      return result ? ProgramBundleSubscriberEntity.fromItemDto(result) : null
    },
  })

  const { mutateAsync: followProgamsInProgramBundle, isPending: isFollowProgamsInProgramBundle } = useMutation({
    mutationFn: async (programIds: string[]) => {
      await programBundleSubscriberApi.followProgramBundle(programBundleId, programIds)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["programBundles", programBundleId],
      })
      void queryClient.invalidateQueries({
        queryKey: ["programBundleSubscription", programBundleId],
      })
      void queryClient.invalidateQueries({
        queryKey: ["programBundlesSubscribed"],
      })
      void queryClient.invalidateQueries({
        queryKey: ["trainingSessions"],
      })
      void queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === "programOnOffProgression",
      })

      toast.success(word("programBundle.follow.add.success"), {
        classNames: successToastClassName(),
      })
    },
    onError: (e) => {
      toast.error(word("programBundle.follow.add.error"), {
        description: e.message,
        classNames: errorToastClassName(),
      })
    },
  })

  const { mutateAsync: unFollowProgamsInProgramBundle, isPending: isUnFollowProgamsInProgramBundle } = useMutation({
    mutationFn: async (programIds: string[]) => {
      await programBundleSubscriberApi.unfollowProgramBundle(programBundleId, programIds)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["programBundles", programBundleId],
      })
      void queryClient.invalidateQueries({
        queryKey: ["programBundleSubscription", programBundleId],
      })
      void queryClient.invalidateQueries({
        queryKey: ["programBundlesSubscribed"],
      })
      void queryClient.invalidateQueries({
        queryKey: ["trainingSessions"],
      })
      void queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === "programOnOffProgression",
      })

      toast.success(word("programBundle.follow.remove.success"), {
        classNames: successToastClassName(),
      })
    },
    onError: (e) => {
      toast.error(word("programBundle.follow.remove.error"), {
        description: e.message,
        classNames: errorToastClassName(),
      })
    },
  })

  const isPending = isFollowProgamsInProgramBundle || isUnFollowProgamsInProgramBundle

  return {
    data,
    isLoading,
    followProgamsInProgramBundle,
    unFollowProgamsInProgramBundle,
    isPending,
  }
}

export const useProgramBundleAthleteSubscription = (programBundle: ProgramBundleEntity) => {
  const [isUpdating, setIsUpdating] = useState(false)
  const { userFinanceApi, programBundleSubscriberApi } = useServicesContext()
  const confirmPopup = useConfirmPopup()
  const { sidebarNavigateTo, setCurrentStackState } = useSidebar()
  const queryClient = useQueryClient()
  const returnUrl = `${ConfigService.config.WEB_URL}${getMySpacePath(Path.MySpace.Training)}`
  const { isAuthor } = useProgramBundleRights(programBundle.id)
  const isOnOffProgramBundle = programBundle.type === ProgramBundleType.ON_OFF
  const { data: programBundleStripeSubscriptionInfos, isLoading } = useProgramBundleSubscription(programBundle.id)
  const { checkoutPayment } = useCheckoutPayment()
  const { addFilteredPrograms } = useTrainingProgramFilterActions()
  const { getStripePublicKey } = useStripePlatform()

  const subscribe = async (): Promise<boolean> => {
    const isInPendingPayment =
      programBundleStripeSubscriptionInfos?.subscriptionStatus === ProgramSubscriptionStatus.PENDING_PAYMENT
    const isInactive =
      programBundleStripeSubscriptionInfos?.subscriptionStatus === ProgramSubscriptionStatus.INACTIVE ||
      !programBundleStripeSubscriptionInfos
    const canSubscribeToProgramBundle = isInactive || isInPendingPayment

    if (!programBundle || !canSubscribeToProgramBundle) {
      return false
    }
    if (!programBundle.price) {
      return await joinProgramBundle()
    }
    return await joinProgramBundleWithPayment()
  }

  const joinProgramBundle = async (): Promise<boolean> => {
    try {
      setIsUpdating(true)

      const { subscriptionStatus } = await programBundleSubscriberApi.subscribeProgramBundle(programBundle.id)

      await queryClient.invalidateQueries({
        queryKey: ["programBundleSubscription", programBundle.id],
      })
      await queryClient.invalidateQueries({
        queryKey: ["programBundle", programBundle.id],
      })
      await queryClient.invalidateQueries({
        queryKey: ["programBundlesSubscribed"],
      })
      await queryClient.invalidateQueries({
        queryKey: ["trainingSessions"],
      })
      await queryClient.invalidateQueries({
        queryKey: ["programs"],
      })
      await queryClient.invalidateQueries({
        queryKey: ["program"],
      })
      await queryClient.invalidateQueries({
        queryKey: ["programSessions"],
      })

      if (programBundle.programs) addFilteredPrograms(programBundle.programs) // Add programs to filtered programs on training page

      if (subscriptionStatus === ProgramSubscriptionStatus.PENDING_REQUEST) {
        toast.success(word("program.subscription.success.toast"), {
          classNames: successToastClassName(),
        })
      }
      if (subscriptionStatus === ProgramSubscriptionStatus.ACTIVE) {
        toast.success(
          word(isOnOffProgramBundle ? "program.onOff.buy.success.toast" : "program.subscribe.success.toast"),
          {
            classNames: successToastClassName(),
          },
        )

        setTimeout(() => {
          setCurrentStackState("activeIndex", ProgramBundleDetailsTabs.PROGRAMS)
        }, 600)
      }
      return true
    } catch (e: unknown) {
      if (
        typeof e === "object" &&
        e !== null &&
        "code" in e &&
        (e as { code?: string }).code === "PAID_PROGRAM_SUBSCRIPTION"
      ) {
        console.warn(LoggerType.Program + "try to free subscribe on paid program, refresh program bundle` ...")
        queryClient.invalidateQueries({
          queryKey: ["programBundle", programBundle.id],
        })
      } else {
        console.error(LoggerType.Program + "subscribe", String(e))
        toast.error(word(isOnOffProgramBundle ? "program.onOff.buy.error.toast" : "program.subscribe.error.toast"), {
          classNames: errorToastClassName(),
        })
      }
      return false
    } finally {
      setIsUpdating(false)
    }
  }

  const joinProgramBundleWithPayment = async (): Promise<boolean> => {
    try {
      setIsUpdating(true)
      const result = await checkoutPayment({ program: programBundle, returnUrl })
      const publicKey = await getStripePublicKey(result.coachStripePlatform)
      const stripe = await loadStripe(publicKey, {
        stripeAccount: result.coachStripeAccountId,
      })

      if (stripe) {
        void sidebarNavigateTo({
          content: (
            <ErrorBoundary fallback={<ErrorView />}>
              <Suspense fallback={<LoadingSpinner />}>
                <CheckoutContainer clientSecret={result.clientSecret} stripePromise={stripe} program={programBundle} />
              </Suspense>
            </ErrorBoundary>
          ),
          leftButtonColor: "var(--textDefault-body)",
        })
      }

      return Promise.resolve(true)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      if (e !== CANCELED_BY_USER) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (e.code === "PROGRAM_HAS_NO_PRICE") {
          console.warn(LoggerType.Program + "try to paid a free program, refresh program ...")
          await queryClient.invalidateQueries({
            queryKey: ["programBundle", programBundle.id],
          })
        } else {
          await queryClient.invalidateQueries({
            queryKey: ["programBundleSubscription", programBundle.id],
          })
          console.error(LoggerType.Stripe + "subscribe on paid program", e)
          toast.error(word(isOnOffProgramBundle ? "program.onOff.buy.error.toast" : "program.subscribe.error.toast"), {
            classNames: errorToastClassName(),
          })
        }
      }
      return false
    } finally {
      setIsUpdating(false)
    }
  }

  const tryNewPayment = async (): Promise<boolean> => {
    if (programBundleStripeSubscriptionInfos?.subscriptionId != null) {
      try {
        setIsUpdating(true)
        const url = await userFinanceApi.getPaymentAuthenticationUrl(
          returnUrl,
          undefined,
          programBundleStripeSubscriptionInfos.programBundleSubscriptionId,
        )

        window.open(url, "_blank", "noreferrer")
        return Promise.resolve(true)
      } catch (e) {
        console.error(LoggerType.Program + "get authentication url failed", e)
        toast.error(word("program.payment.authentication.error.toast"), {
          classNames: errorToastClassName(),
        })
        return false
      } finally {
        setIsUpdating(false)
      }
    }
    return false
  }

  const openPaymentSettings = async (): Promise<boolean> => {
    try {
      setIsUpdating(true)
      const result = await checkoutPayment({ program: programBundle, returnUrl, shouldReceiveClientSecret: false })

      window.open(result.url, "_blank", "noreferrer")

      return Promise.resolve(true)
    } catch (e: any) {
      console.error(LoggerType.Program + "get payment settings url failed", e)
      toast.error(word("program.payment.authentication.error.toast"), {
        classNames: errorToastClassName(),
      })
      return false
    } finally {
      setIsUpdating(false)
    }
  }

  const unsubscribe = async (): Promise<void> => {
    const isActive = programBundleStripeSubscriptionInfos?.subscriptionStatus === ProgramSubscriptionStatus.ACTIVE
    const canUnsubscribeToProgramBundle =
      isActive ||
      (programBundleStripeSubscriptionInfos &&
        (programBundleStripeSubscriptionInfos.subscriptionStatus === ProgramSubscriptionStatus.PENDING_PAYMENT ||
          programBundleStripeSubscriptionInfos.subscriptionStatus === ProgramSubscriptionStatus.TRIALING))

    if (programBundle && !isAuthor && canUnsubscribeToProgramBundle) {
      try {
        const processUnsubscribe = async () => {
          setIsUpdating(true)
          await programBundleSubscriberApi.unsubscribeProgramBundle(programBundle.id)

          await queryClient.invalidateQueries({
            queryKey: ["programBundleSubscription", programBundle.id],
          })
          await queryClient.invalidateQueries({
            queryKey: ["programBundle", programBundle.id],
          })
          await queryClient.invalidateQueries({
            queryKey: ["programBundlesSubscribed"],
          })
          await queryClient.invalidateQueries({
            queryKey: ["trainingSessions"],
          })
          await queryClient.invalidateQueries({
            queryKey: ["programs"],
          })
          await queryClient.invalidateQueries({
            queryKey: ["program"],
          })
          await queryClient.invalidateQueries({
            queryKey: ["programSessions"],
          })

          // await planningService.refreshSessions() // Refetch sessions of last opened program
          toast.success(word("program.unsubscribe.success.toast"), {
            classNames: successToastClassName(),
          })
          setIsUpdating(false)
        }

        if (programBundleStripeSubscriptionInfos.periodEndDate) {
          const isTrial = programBundleStripeSubscriptionInfos.subscriptionStatus === ProgramSubscriptionStatus.TRIALING
          const formattedEndDate = getFormatedDateTime(programBundleStripeSubscriptionInfos.periodEndDate)

          confirmPopup.show({
            title: word("program.subscription.stop.title"),
            message: isTrial
              ? word("program.subscription.stop.trial.body", {
                  endDateSubscription: formattedEndDate,
                })
              : word("program.subscription.stop.paid.body", {
                  endDateSubscription: formattedEndDate,
                }),
            accept: async () => {
              await processUnsubscribe()
            },
            footerProps: {
              confirmText: word("program.subscription.stop.button"),
              cancelText: word("global.cancel"),
            },
          })
        } else if (!isActive) {
          confirmPopup.show({
            title: word("program.details.pendingPayment.popup.title"),
            message: word("program.details.pendingPayment.popup.description"),
            accept: async () => {
              await processUnsubscribe()
            },
          })
        } else {
          await processUnsubscribe()
        }
      } catch (e) {
        console.error(LoggerType.Program + "unsubscribe", e)
        toast.error(word("program.unsubscribe.error.toast"), {
          classNames: errorToastClassName(),
        })
      } finally {
        setIsUpdating(false)
      }
    }
  }

  return {
    isUpdating,
    isLoading,
    subscribe,
    unsubscribe,
    joinProgramBundleWithPayment,
    programBundleStripeSubscriptionInfos,
    tryNewPayment,
    openPaymentSettings,
  }
}
